import React, { useState, useEffect } from 'react';
import { FileText, Download, Eye, X, AlertTriangle } from 'lucide-react';
import ApiService from '../services/apiService';
import TranslationService from '../services/translationService';
import {
  capDataUrl,
  enforceImageBudget,
  MAX_SAT_IMAGE_EDGE_PX,
  MAX_SAT_IMAGE_BYTES,
  MAX_MAP_IMAGE_EDGE_PX,
  MAX_MAP_IMAGE_BYTES
} from '../utils/imageGuards';

/* Sensor keys that are hard-coded to 0 server-side and must never reach the PDF. */
const STRUCTURALLY_DEAD = ['Light_Intensity', 'Chlorophyll_Content', 'Chlorophyll'];
/* 0 is physically impossible for these -> treat 0 as "not installed".
   Must stay in sync with the identical list in views/IoTView.js.
   Ambient_Temperature and Humidity follow the backend's own rule:
   services/iot_manager.py::_apply_weather_fallback_and_log treats 0 as "no
   reading" for both and substitutes Open-Meteo values, so a 0 arriving here
   means the probe and the fallback both failed. Soil_Moisture, Soil_Temperature
   and N/P/K are excluded on purpose - 0 is a real reading for those. */
const ZERO_MEANS_ABSENT = ['Soil_pH', 'Electrochemical_Signal', 'Soil_Salinity',
  'Salinity', 'TDS', 'Epsilon', 'Ambient_Temperature', 'Humidity'];

/**
 * Defensive re-filter of iot_data on its way to the wire.
 * A sensor that is not installed is ABSENT - never null, never 0.
 */
const sanitizeIotData = (raw) => {
  const out = {};
  Object.entries(raw || {}).forEach(([rawKey, v]) => {
    const key = (rawKey === 'Electrochemical_Signal') ? 'Soil_Salinity' : rawKey;
    if (STRUCTURALLY_DEAD.includes(key)) return;
    if (v === null || v === undefined) return;
    const n = Number(v);
    if (!Number.isFinite(n)) return;
    if (n === 0 && ZERO_MEANS_ABSENT.includes(key)) return;
    out[key] = n;
  });
  return out;
};

/** Runs `worker` over `items` with a bounded number of in-flight tasks. */
const runWithConcurrency = async (items, limit, worker) => {
  const results = new Array(items.length);
  let cursor = 0;
  const runners = Array.from({ length: Math.max(1, Math.min(limit, items.length)) }, async () => {
    for (;;) {
      const i = cursor++;
      if (i >= items.length) return;
      results[i] = await worker(items[i], i);
    }
  });
  await Promise.all(runners);
  return results;
};

const ExportReportModal = ({ isOpen, onClose, reportData, currentLang, t, isRTL, userFarms = [], userId }) => {
  const [loading, setLoading] = useState(false);
  const [statusText, setStatusText] = useState("");
  const [notice, setNotice] = useState("");
  const [previewUrl, setPreviewUrl] = useState(null);
  const [pdfBlob, setPdfBlob] = useState(null);

  const [formData, setFormData] = useState({
    farmName: "",
    reporterName: "",
    lang: currentLang
  });

  const [sections, setSections] = useState({
    iot: true, weather: true, history: true, device_map: true,
    leaf: true, soil: true, crop: true, yield: true, advanced_ai: true,
    satellite: true, accounting: true, control: true, consultant: true
  });

  useEffect(() => {
    if (isOpen) {
      setFormData(prev => ({ ...prev, lang: currentLang }));
      setPreviewUrl(null);
      setPdfBlob(null);
      setNotice("");
    }
  }, [isOpen, currentLang]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setPreviewUrl(null);
    setPdfBlob(null);
  };

  const handleSectionChange = (id, checked) => {
    setSections(prev => ({ ...prev, [id]: checked }));
    setPreviewUrl(null);
    setPdfBlob(null);
  };

  /* ---------------------------------------------------------------
     EXECUTION-STATE DETECTION
     A module that RAN counts as having data even when its result set
     is legitimately empty. `module_runs` is the authoritative marker;
     the payload-shape probe is only a legacy fallback.
     --------------------------------------------------------------- */
  /* A module that ran and FAILED is not evidence that there is anything to
     print. Each gate below is `ran(id) || <has data>`, so treating a failed run
     as "ran" offered the user a section whose producer had just errored - and
     if that producer left the previous device's data behind, the section was
     not merely empty, it was wrong. A failed module now falls through to its
     data check, which is the honest question. */
  const ran = (id) => {
    const r = reportData?.module_runs?.[id];
    return Boolean(r?.ran && r.ok !== false);
  };

  const hasIot        = ran('iot')         || Boolean(reportData?.iot_data && Object.keys(reportData.iot_data).length > 0);
  const hasWeather    = ran('weather')     || Boolean(reportData?.weather_data && Object.keys(reportData.weather_data).length > 0);
  const hasHistory    = ran('history')     || Boolean(Array.isArray(reportData?.iot_history?.points) && reportData.iot_history.points.length > 0);
  const hasDeviceMap  = ran('device_map')  || Boolean(Array.isArray(reportData?.device_imagery) && reportData.device_imagery.length > 0);
  const hasLeaf       = ran('leaf')        || Boolean(reportData?.leaf_diagnosis?.diagnosis);
  const hasSoil       = ran('soil')        || Boolean(reportData?.soil_classification?.soil_type);
  const hasCrop       = ran('crop')        || Boolean(Array.isArray(reportData?.crop_recommendations) && reportData.crop_recommendations.length > 0);
  const hasYield      = ran('yield')       || Boolean(reportData?.yield_prediction && typeof reportData.yield_prediction.predicted_yield_kg_per_ha === 'number');
  const hasAdvancedAi = ran('advanced_ai') || Boolean(Array.isArray(reportData?.advanced_ai?.turns) && reportData.advanced_ai.turns.length > 0);
  const hasSatellite  = ran('satellite')   || Boolean(reportData?.satellite_data && Object.keys(reportData.satellite_data).length > 0);
  const hasAccounting = ran('accounting')  || Boolean(reportData?.accounting_summary && (Number(reportData.accounting_summary.total_revenue) > 0 || Number(reportData.accounting_summary.total_expenses) > 0));
  const hasControl    = ran('control')     || Boolean(reportData?.control_automation?.actuators?.length || reportData?.control_automation?.schedules?.length);
  const hasConsultant = ran('consultant')  || Boolean(reportData?.ai_consultant_text);

  if (!isOpen) return null;

  const SECTION_ROWS = [
    { id: 'iot',         label: t.exportModal?.iot         || 'IoT Sensor Readings',    hasData: hasIot },
    { id: 'weather',     label: t.exportModal?.weather     || 'Live Weather',           hasData: hasWeather },
    { id: 'history',     label: t.exportModal?.history     || 'Historical Readings',    hasData: hasHistory },
    { id: 'device_map',  label: t.exportModal?.deviceMap   || 'Device Satellite Map',   hasData: hasDeviceMap },
    { id: 'leaf',        label: t.exportModal?.leaf        || 'Leaf Disease Detection', hasData: hasLeaf },
    { id: 'soil',        label: t.exportModal?.soil        || 'Soil Type Detection',    hasData: hasSoil },
    { id: 'crop',        label: t.exportModal?.crop        || 'Crop Recommendation',    hasData: hasCrop },
    { id: 'yield',       label: t.exportModal?.yield       || 'Yield Estimator',        hasData: hasYield },
    { id: 'advanced_ai', label: t.exportModal?.advancedAi  || 'Advanced AI Consultant', hasData: hasAdvancedAi },
    { id: 'satellite',   label: t.exportModal?.satellite   || 'Satellite Imagery',      hasData: hasSatellite },
    { id: 'accounting',  label: t.exportModal?.accounting  || 'Farm Accounting',        hasData: hasAccounting },
    { id: 'control',     label: t.exportModal?.control     || 'Control & Automation',   hasData: hasControl },
    { id: 'consultant',  label: t.exportModal?.consultant  || 'AI Consultant',          hasData: hasConsultant },
  ];

  const generatePdfBlob = async () => {
    const reportLang = formData.lang || "en";
    const tRep = TranslationService.getTranslation(reportLang);
    const isReportAr = reportLang === "ar";

    setNotice("");
    /* Counters surfaced to the user instead of failing silently. */
    let satFailures = 0;
    let omittedImages = 0;

    /* Downscale a data URI to the given budget. Returns null (and counts an
       omission) when the image cannot be brought under the cap. */
    const capImage = async (dataUrl, opts) => {
      if (!dataUrl || typeof dataUrl !== 'string' || dataUrl.indexOf('data:') !== 0) return null;
      try {
        const out = await capDataUrl(dataUrl, opts);
        if (!out) { omittedImages += 1; return null; }
        return out;
      } catch (e) {
        omittedImages += 1;
        return null;
      }
    };

    /* ---------------- Satellite imagery ---------------- */
    const transformedSatelliteData = {};

    if (sections.satellite && hasSatellite && reportData.satellite_data) {
      setStatusText(t.exportModal?.statusSat || "Downloading Satellite Images...");

      /* 15s budget per request, timer armed WHEN the request starts, 1 retry. */
      const fetchImageB64 = async (url) => {
        if (!url) return { data: null, attempted: false };
        const fullUrl = url.startsWith('http') ? url : `${ApiService.BASE_URL}${url}`;
        for (let attempt = 0; attempt < 2; attempt++) {
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 15000);
          try {
            const res = await fetch(fullUrl, { signal: controller.signal });
            clearTimeout(timeoutId);
            if (!res.ok) continue;
            const blob = await res.blob();
            const dataUrl = await new Promise((resolve) => {
              const reader = new FileReader();
              reader.onloadend = () => resolve(reader.result);
              reader.onerror = () => resolve(null);
              reader.readAsDataURL(blob);
            });
            if (dataUrl) return { data: dataUrl, attempted: true };
          } catch (e) {
            clearTimeout(timeoutId);
          }
        }
        return { data: null, attempted: true };
      };

      const orderedKeys = ['rgb', 'ndvi', 'ndmi', 'ndwi', 'ndre', 'gndvi', 'savi']
        .filter(k => reportData.satellite_data[k]);

      /* One flat task list so concurrency is bounded across ALL panels
         (the old code fired 14 parallel fetches behind a ~6-socket cap). */
      const tasks = [];
      orderedKeys.forEach((key) => {
        const d = reportData.satellite_data[key];
        tasks.push({ key, slot: 'new', url: d.current_url });
        tasks.push({ key, slot: 'old', url: d.past_url });
      });

      const fetched = await runWithConcurrency(tasks, 3, async (task) => {
        const r = await fetchImageB64(task.url);
        if (r.attempted && !r.data) satFailures += 1;
        const capped = r.data
          ? await capImage(r.data, { maxEdge: MAX_SAT_IMAGE_EDGE_PX, maxBytes: MAX_SAT_IMAGE_BYTES })
          : null;
        return { ...task, map: capped, failed: Boolean(r.attempted && !capped) };
      });

      const mapByKey = {};
      fetched.forEach((r) => {
        if (!r) return;
        mapByKey[`${r.slot}_${r.key}`] = r;
      });

      orderedKeys.forEach((key) => {
        const d = reportData.satellite_data[key];
        const nw = mapByKey[`new_${key}`] || {};
        const od = mapByKey[`old_${key}`] || {};
        transformedSatelliteData[`new_${key}`] = {
          map: nw.map || null,
          unavailable: Boolean(nw.failed),
          val: d.current_val ? Number(Number(d.current_val).toFixed(2)) : 0,
          insight: key === 'rgb' ? "" : (tRep.satellite?.[d.current_insight] || d.current_insight || "N/A")
        };
        transformedSatelliteData[`old_${key}`] = {
          map: od.map || null,
          unavailable: Boolean(od.failed),
          val: d.past_val ? Number(Number(d.past_val).toFixed(2)) : 0,
          insight: key === 'rgb' ? "" : (tRep.satellite?.[d.past_insight] || d.past_insight || "N/A")
        };
      });
    }

    /* ---------------- Photographic evidence ---------------- */
    setStatusText(t.exportModal?.statusImages || "Preparing images...");

    const leafInputImage = (sections.leaf && hasLeaf)
      ? await capImage(reportData.leaf_input_image_base64) : null;
    const leafMaskImage = (sections.leaf && hasLeaf)
      ? await capImage(reportData.leaf_diagnosis?.mask_image_base64) : null;
    const soilInputImage = (sections.soil && hasSoil)
      ? await capImage(reportData.soil_input_image_base64) : null;

    let deviceImagery = [];
    if (sections.device_map && hasDeviceMap && Array.isArray(reportData.device_imagery)) {
      const src = reportData.device_imagery.slice(0, 4);
      deviceImagery = await Promise.all(src.map(async (dev) => ({
        ...dev,
        image_base64: await capImage(dev?.image_base64, {
          maxEdge: MAX_MAP_IMAGE_EDGE_PX, maxBytes: MAX_MAP_IMAGE_BYTES
        })
      })));
    }

    let advancedAi = {};
    if (sections.advanced_ai && hasAdvancedAi && reportData.advanced_ai) {
      const src = reportData.advanced_ai;
      const atts = Array.isArray(src.attachments) ? src.attachments.slice(0, 2) : [];
      advancedAi = {
        ...src,
        turns: Array.isArray(src.turns) ? src.turns.slice(-6) : [],
        attachments: await Promise.all(atts.map(async (a) => ({
          name: a?.name || "",
          mime: a?.mime || "image/jpeg",
          image_base64: await capImage(a?.image_base64)
        })))
      };
      advancedAi.attachments = advancedAi.attachments.filter(a => Boolean(a.image_base64));
    }

    /* ---------------- Label localisation ---------------- */
    setStatusText(t.exportModal?.statusAssemble || "Translating & Assembling Data...");

    let finalIotStatus = reportData.iot_ai_status || "Unknown";
    if (finalIotStatus === "Healthy" || finalIotStatus === "صحة جيدة") finalIotStatus = tRep.dashboard.healthy;
    else if (finalIotStatus.includes("Stress") || finalIotStatus.includes("إجهاد")) finalIotStatus = tRep.dashboard.stress;

    let finalLeafDiag = reportData.leaf_diagnosis?.diagnosis || "";
    if (finalLeafDiag) {
      const snaked = finalLeafDiag.replace(/ - /g, '___').replace(/ /g, '_');
      finalLeafDiag = tRep.leaf?.diseases?.[finalLeafDiag] || tRep.leaf?.diseases?.[snaked] || finalLeafDiag;
    }

    let finalSoil = reportData.soil_classification?.soil_type || "";
    finalSoil = tRep.crops?.soil_types?.[finalSoil] || finalSoil;

    const finalCrops = (reportData.crop_recommendations || []).slice(0, 5).map(c => ({
      ...c,
      crop: tRep.crops?.crop_names?.[c.crop] || c.crop
    }));

    const finalCropInputs = {};
    if (reportData.crop_inputs && Object.keys(reportData.crop_inputs).length > 0) {
      const ci = reportData.crop_inputs;
      finalCropInputs[tRep.crops.inputs.n] = ci.N;
      finalCropInputs[tRep.crops.inputs.p] = ci.P;
      finalCropInputs[tRep.crops.inputs.k] = ci.K;
      finalCropInputs[tRep.crops.inputs.temp] = ci.temperature;
      finalCropInputs[tRep.crops.inputs.hum] = ci.humidity;
      finalCropInputs[tRep.crops.inputs.ph] = ci.ph;
      finalCropInputs[tRep.crops.waterLabel] = ci.rainfall;
    }

    const finalYieldInputs = {};
    if (reportData.yield_inputs && Object.keys(reportData.yield_inputs).length > 0) {
      const yi = reportData.yield_inputs;
      finalYieldInputs[tRep.yield.inputs.crop] = tRep.yield.crops?.[yi.Crop] || yi.Crop;
      finalYieldInputs[tRep.yield.inputs.water] = yi.Rainfall;
      finalYieldInputs[tRep.yield.inputs.n] = yi.N;
      finalYieldInputs[tRep.yield.inputs.p] = yi.P;
      finalYieldInputs[tRep.yield.inputs.k] = yi.K;
      finalYieldInputs[tRep.yield.inputs.temp] = yi.Temperature;
      finalYieldInputs[tRep.yield.inputs.hum] = yi.Humidity;
      finalYieldInputs[tRep.yield.inputs.ph] = yi.pH;
    }

    /* Only the consultant text is translated at export time - the other
       markdown blocks would fan out one fetch per line and get rate-limited. */
    let finalAiText = reportData.ai_consultant_text || "";
    if (reportLang !== currentLang && finalAiText) {
      setStatusText(t.exportModal?.statusTranslate || "Translating AI output...");
      finalAiText = await TranslationService.translateText(finalAiText, reportLang);
    }

    /* ---------------- Payload assembly ---------------- */
    const cleanIot = sanitizeIotData(reportData.iot_data);
    const declaredSensors = Array.isArray(reportData.iot_sensors_available)
      ? reportData.iot_sensors_available.filter(k => Object.prototype.hasOwnProperty.call(cleanIot, k))
      : [];
    const iotSensorsAvailable = declaredSensors.length > 0 ? declaredSensors : Object.keys(cleanIot);

    let payload = {
      options: {
        include_iot:         Boolean(sections.iot && hasIot),
        include_weather:     Boolean(sections.weather && hasWeather),
        include_history:     Boolean(sections.history && hasHistory),
        include_device_map:  Boolean(sections.device_map && hasDeviceMap),
        include_leaf_ai:     Boolean(sections.leaf && hasLeaf),
        include_soil_ai:     Boolean(sections.soil && hasSoil),
        include_crop_ai:     Boolean(sections.crop && hasCrop),
        include_yield_ai:    Boolean(sections.yield && hasYield),
        include_advanced_ai: Boolean(sections.advanced_ai && hasAdvancedAi),
        include_satellite:   Boolean(sections.satellite && hasSatellite),
        include_accounting:  Boolean(sections.accounting && hasAccounting),
        include_control:     Boolean(sections.control && hasControl),
        include_consultant:  Boolean(sections.consultant && hasConsultant),
        language: reportLang
      },
      farm_name: formData.farmName || (t.exportModal?.defaultFarmName || (isReportAr ? "مزرعة ذكية" : "Smart Farm")),
      farmer_name: formData.reporterName || (t.exportModal?.defaultReporter || (isReportAr ? "مدير النظام" : "Admin")),
      logo_url: null,
      user_id: userId || 1,
      schema_version: 2,

      // ---- IoT ----
      iot_data: cleanIot,
      iot_sensors_available: iotSensorsAvailable,
      iot_device: reportData.iot_device || {},
      iot_ai_status: finalIotStatus,
      iot_ai_confidence: Number(reportData.iot_ai_confidence) || 0,
      iot_rag_text: reportData.iot_rag_text || "",

      // ---- IoT sub-sections ----
      weather_data: reportData.weather_data || {},
      iot_history: reportData.iot_history || {},
      device_imagery: deviceImagery,

      // ---- Leaf ----
      leaf_diagnosis: {
        diagnosis: finalLeafDiag,
        confidence: reportData.leaf_diagnosis?.confidence || 0,
        leaves_detected: (typeof reportData.leaf_diagnosis?.leaves_detected === 'number')
          ? reportData.leaf_diagnosis.leaves_detected : null,
        mask_image_base64: leafMaskImage
      },
      leaf_input_image_base64: leafInputImage,

      // ---- Soil ----
      soil_classification: {
        soil_type: finalSoil,
        confidence: reportData.soil_classification?.confidence || 0
      },
      soil_input_image_base64: soilInputImage,
      soil_ai_text: reportData.soil_ai_text || "",

      // ---- Crop ----
      crop_inputs: finalCropInputs,
      crop_recommendations: finalCrops,
      crop_ai_text: reportData.crop_ai_text || "",

      // ---- Yield ----
      yield_inputs: finalYieldInputs,
      yield_prediction: reportData.yield_prediction || {},
      yield_ai_text: reportData.yield_ai_text || "",

      // ---- Advanced AI ----
      advanced_ai: advancedAi,

      // ---- Satellite ----
      satellite_data: transformedSatelliteData,
      sat_date_current: reportData.sat_date_current || "N/A",
      sat_date_past: reportData.sat_date_past || "N/A",
      satellite_area: Number(reportData.satellite_area) || 0,

      // ---- Accounting ----
      accounting_summary: reportData.accounting_summary || {},
      accounting_revenue_breakdown: Array.isArray(reportData.accounting_revenue_breakdown)
        ? reportData.accounting_revenue_breakdown : [],
      accounting_expense_breakdown: Array.isArray(reportData.accounting_expense_breakdown)
        ? reportData.accounting_expense_breakdown : [],
      accounting_ai_text: reportData.accounting_ai_text || "",

      // ---- Control & Automation ----
      control_automation: reportData.control_automation || {},
      control_ai_text: reportData.control_ai_text || "",

      // ---- Consultant ----
      ai_consultant_text: finalAiText,

      // ---- Execution markers ----
      module_runs: reportData.module_runs || {}
    };

    /* Whole-payload image budget. Drops in the contract's priority order. */
    let budgetDropped = [];
    try {
      const guarded = enforceImageBudget(payload);
      if (guarded && guarded.payload) {
        payload = guarded.payload;
        budgetDropped = Array.isArray(guarded.dropped) ? guarded.dropped : [];
      }
    } catch (e) {
      console.warn("Image budget enforcement failed, sending payload as-is:", e);
    }

    const notes = [];
    if (satFailures > 0) {
      notes.push(isRTL
        ? `تعذر تحميل ${satFailures} من صور الأقمار الصناعية.`
        : `${satFailures} satellite image(s) could not be downloaded.`);
    }
    if (omittedImages > 0) {
      notes.push(isRTL
        ? `تم استبعاد ${omittedImages} صورة لتعذر ضغطها.`
        : `${omittedImages} image(s) were omitted because they could not be compressed.`);
    }
    if (budgetDropped.length > 0) {
      notes.push(t.exportModal?.sizeWarning
        || "Some images were omitted to keep the report under the size limit.");
    }
    setNotice(notes.join(' '));

    setStatusText(t.exportModal?.statusGen || "Generating PDF...");

    /* postPdf verifies the response is a non-empty application/pdf and turns
       anything else into one readable sentence, so there is nothing left to
       re-check here. Deduct only once we actually hold the file - a failed
       export must not move the displayed balance. */
    const blob = await ApiService.postPdf('/generate-report', payload);
    window.dispatchEvent(new CustomEvent('credit-deducted', { detail: { amount: 5 } }));

    return blob;
  };

  const handlePreview = async () => {
    setLoading(true);
    try {
      /* Reuse the cached PDF, exactly as handleDownload does. Every path that
         can invalidate it already nulls pdfBlob (open, handleInputChange,
         handleSectionChange), so a cached blob always matches the current
         options - and without this check a second Preview click spent another
         5 credits regenerating a byte-identical report. */
      let blob = pdfBlob;
      if (!blob) {
        blob = await generatePdfBlob();
        setPdfBlob(blob);
      }
      const url = window.URL.createObjectURL(blob);
      setPreviewUrl(url);
    } catch (error) {
      console.error("Preview Error:", error);
      const title = t.exportModal?.previewFailed || (isRTL ? "فشل المعاينة" : "Preview Failed");
      alert(`${title}:\n\n${error.message}`);
    } finally {
      setLoading(false);
      setStatusText("");
    }
  };

  const handleDownload = async () => {
    setLoading(true);
    try {
      let blobToDownload = pdfBlob;
      if (!blobToDownload) {
        blobToDownload = await generatePdfBlob();
      }

      const url = window.URL.createObjectURL(blobToDownload);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `SmartFarm_Report_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);

      onClose();
    } catch (error) {
      console.error("Export Error:", error);
      const title = t.exportModal?.exportFailed || (isRTL ? "فشل التصدير" : "Export Failed");
      alert(`${title}:\n\n${error.message}`);
    } finally {
      setLoading(false);
      setStatusText("");
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-geo-navy/60 dark:bg-black/80 backdrop-blur-md flex items-center justify-center p-4 transition-all" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className={`bg-white dark:bg-geo-navy border border-gray-200 dark:border-white/10 rounded-3xl w-full ${previewUrl ? 'max-w-7xl h-[95vh]' : 'max-w-3xl h-[90vh] md:h-auto'} p-8 shadow-2xl flex flex-col transition-all duration-300`}>

        {/* Header */}
        <div className="flex justify-between items-center mb-6 shrink-0 border-b border-gray-100 dark:border-white/10 pb-4">
          <h2 className="text-2xl font-black text-geo-navy dark:text-white flex items-center gap-3">
            <div className="p-2.5 bg-geo-copper/10 rounded-xl">
              <FileText className="text-geo-copper" size={24} />
            </div>
            {t.common?.export || "Export Complete Report"}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-geo-navy dark:hover:text-white transition-colors p-2 bg-gray-50 dark:bg-slate-800 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-full">
            <X size={24} />
          </button>
        </div>

        <div className="flex flex-col md:flex-row flex-1 gap-8 overflow-hidden">
          {/* Left Side: Configuration Form */}
          <div className={`flex flex-col flex-1 h-full ${previewUrl ? 'md:max-w-sm border-r border-gray-100 dark:border-white/10 pr-6' : 'w-full'}`}>
            <div className="flex-1 overflow-y-auto pr-2 space-y-6 custom-scrollbar">
              <div className="space-y-4">
                <div>
                  <label className="text-[11px] uppercase tracking-wider font-bold text-gray-500 dark:text-gray-400 mb-1 ml-1 block">{t.common?.farmName || "Farm Name"}</label>
                  {userFarms && userFarms.length > 0 ? (
                    <select
                      value={formData.farmName}
                      onChange={e => handleInputChange('farmName', e.target.value)}
                      className="w-full bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl p-3 text-geo-navy dark:text-white font-semibold focus:border-geo-copper focus:bg-white outline-none shadow-inner transition-colors cursor-pointer"
                    >
                      <option value="">{isRTL ? "اختر مزرعة..." : "Select a farm..."}</option>
                      {userFarms.map(farm => (
                        <option key={farm.id} value={farm.farm_name || farm.address}>
                          {farm.farm_name || farm.address}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input type="text" value={formData.farmName} onChange={e => handleInputChange('farmName', e.target.value)} className="w-full bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl p-3 text-geo-navy dark:text-white font-semibold focus:border-geo-copper focus:bg-white outline-none shadow-inner transition-colors" />
                  )}
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-wider font-bold text-gray-500 dark:text-gray-400 mb-1 ml-1 block">{t.common?.reporterName || "Reporter Name"}</label>
                  <input type="text" value={formData.reporterName} onChange={e => handleInputChange('reporterName', e.target.value)} className="w-full bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl p-3 text-geo-navy dark:text-white font-semibold focus:border-geo-copper focus:bg-white outline-none shadow-inner transition-colors" />
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-wider font-bold text-gray-500 dark:text-gray-400 mb-1 ml-1 block">{t.exportModal?.reportLang || "Report Language"}</label>
                  <select value={formData.lang} onChange={e => handleInputChange('lang', e.target.value)} className="w-full bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl p-3 text-geo-navy dark:text-white font-semibold outline-none cursor-pointer">
                    <option value="en">English (Professional)</option>
                    <option value="ar">العربية (رسمي)</option>
                  </select>
                </div>
              </div>

              <div>
                <h3 className="text-xs uppercase tracking-widest font-bold text-geo-copper mb-3">
                  {t.exportModal?.includedSections || "Included Data Modules"}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {SECTION_ROWS.map(sec => (
                    <label
                      key={sec.id}
                      title={sec.hasData ? undefined : (t.exportModal?.noDataYet || 'No data generated yet')}
                      className={`flex items-center gap-3 p-3 rounded-xl border transition-all ${sec.hasData ? 'bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-700 cursor-pointer hover:border-geo-olive dark:hover:border-geo-olive shadow-sm transform hover:-translate-y-0.5' : 'bg-gray-50 dark:bg-slate-900 border-transparent opacity-50 cursor-not-allowed'}`}
                    >
                      <input type="checkbox" disabled={!sec.hasData} checked={Boolean(sections[sec.id] && sec.hasData)} onChange={e => handleSectionChange(sec.id, e.target.checked)} className="mt-0 accent-geo-olive w-4 h-4 rounded shrink-0 cursor-pointer" />
                      <span className="text-geo-navy dark:text-gray-200 font-semibold text-sm leading-tight">{sec.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            <div className="mt-6 pt-5 border-t border-gray-100 dark:border-white/10 flex flex-col gap-3 shrink-0">
              {notice && (
                <div className="flex items-start gap-2 text-[11px] font-semibold text-geo-copper bg-geo-copper/10 border border-geo-copper/20 rounded-xl px-3 py-2">
                  <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                  <span className="leading-snug">{notice}</span>
                </div>
              )}
              <button onClick={handlePreview} disabled={loading || previewUrl !== null} className="w-full bg-geo-navy hover:bg-slate-800 dark:bg-slate-800 dark:hover:bg-slate-700 text-white py-3.5 rounded-xl font-bold flex items-center justify-center gap-2 shadow-lg transition-transform hover:-translate-y-0.5 active:translate-y-0 disabled:opacity-50">
                {loading && !previewUrl ? <span className="animate-spin"><X size={16} className="text-transparent border-t-white border-2 rounded-full w-5 h-5" /></span> : <Eye size={20} />}
                {loading && !previewUrl ? statusText : (t.exportModal?.previewBtn || "Preview Report")}
              </button>
              <button onClick={handleDownload} disabled={loading} className="w-full bg-geo-copper hover:bg-orange-700 text-white py-3.5 rounded-xl font-bold flex items-center justify-center gap-2 shadow-lg shadow-geo-copper/30 transition-transform hover:-translate-y-0.5 active:translate-y-0">
                <Download size={20} />
                {t.exportModal?.downloadBtn || "Download PDF"}
              </button>
            </div>
          </div>

          {/* Right Side: Live PDF Preview Window */}
          {previewUrl && (
            <div className="flex-1 bg-gray-100 dark:bg-slate-950 rounded-2xl overflow-hidden border border-gray-200 dark:border-slate-800 flex flex-col shadow-inner">
              <div className="bg-white dark:bg-slate-900 p-3 flex justify-between items-center border-b border-gray-200 dark:border-slate-800">
                <span className="text-geo-navy dark:text-white text-sm font-bold flex items-center gap-2">
                  <FileText size={16} className="text-geo-copper" /> {t.exportModal?.previewTitle || "Report Document Preview"}
                </span>
                <button onClick={() => setPreviewUrl(null)} className="text-gray-400 hover:text-red-500 flex items-center gap-1 text-xs font-bold bg-gray-50 dark:bg-slate-800 hover:bg-red-50 dark:hover:bg-rose-900/30 px-3 py-1.5 rounded-lg transition-colors border border-gray-100 dark:border-slate-700">
                  <X size={14} /> {t.exportModal?.closePreview || "Close Preview"}
                </button>
              </div>
              <iframe src={`${previewUrl}#toolbar=0`} className="w-full h-full bg-white dark:bg-gray-200 border-none" title={t.exportModal?.previewBtn} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExportReportModal;
